<template>
	<a-modal
		class="g-pay-video-player-edit-modal"
		:visible="visible"
		title="单视频付费编辑"
		width="730px"
		:centered="true"
		:mask-closable="false"
		:destroy-on-close="true"
		@cancel="onCancel"
	>
		<div class="g-edit-modal__scroll">
			<PayMediaEditForm
				:form="form"
				mode="duration"
				:max-duration="form.video?.duration || 0"
				:show-title="true"
				:title-placeholder="'暮色下的湖畔灯火'"
				:show-subtitle="false"
			/>
			<div class="g-pay-video-player-edit">
				<div
					class="g-pay-video-player-edit__preview"
					:style="{ width: `${form.playerWidth}%` }"
				>
					<div
						class="g-pay-video-player-edit__shell"
						:style="{ borderRadius: `${form.borderRadius}px` }"
					>
						<UploadProgressPanel
							v-if="isUploading"
							:tasks="uploadTasks"
							variant="item"
							hide-filename
						/>
						<template v-else>
						<img
							v-if="coverUrl && !previewStarted"
							class="g-pay-video-player-edit__cover"
							:src="coverUrl"
							alt=""
						/>
						<video
							v-if="previewVideoUrl"
							ref="previewVideoEl"
							class="g-pay-video-player-edit__video"
							:class="{ 'is-visible': previewStarted }"
							:src="previewVideoUrl"
							controls
							playsinline
							preload="metadata"
							@play="previewStarted = true"
							@ended="onPreviewVideoEnded"
						></video>
						<div v-if="!previewStarted" class="g-pay-video-player-edit__overlay"></div>
						<div v-if="!previewStarted" class="g-pay-video-player-edit__content">
							<span class="g-pay-video-player-edit__duration">{{ form.video?.durationText || '00:00' }}</span>
							<button
								type="button"
								class="g-pay-video-player-edit__play"
								:disabled="!previewVideoUrl"
								aria-label="播放"
								@click="togglePreviewVideo"
							>
								<span class="g-pay-video-player-edit__triangle"></span>
							</button>
						</div>
						</template>
					</div>
				</div>

				<div class="g-pay-video-player-edit__panel">
					<div class="g-pay-video-player-edit__toolbar">
						<button type="button" class="g-pay-video-player-edit__action" @click="triggerVideoSelect">换视频</button>
						<button type="button" class="g-pay-video-player-edit__action" @click="triggerCoverSelect">换封面</button>
					</div>
				</div>
			</div>
		</div>

		<input
			ref="coverInput"
			type="file"
			accept="image/*"
			class="g-pay-video-player-edit__file-input"
			@change="onCoverSelect"
		/>
		<input
			ref="videoInput"
			type="file"
			accept="video/*,.mp4,.mov,.avi,.flv,.mkv,.webm,.m4v,.wmv"
			class="g-pay-video-player-edit__file-input"
			@change="onVideoSelect"
		/>

		<template #footer>
			<a-button @click="onCancel">取消</a-button>
			<a-button type="primary" :disabled="isUploading" @click="onConfirm">确认</a-button>
		</template>
	</a-modal>
</template>

<script>
	import PayMediaEditForm from './PayMediaEditForm.vue';
	import UploadProgressPanel from './UploadProgressPanel.vue';
	import {
		isLocalImageFile,
		MAX_LOCAL_IMAGE_SIZE,
		revokeLocalImageUrl,
	} from '../utils/localImage.js';
	import {
		filterValidLocalVideoList,
		normalizeLocalVideoItem,
		revokeLocalVideoItem,
	} from '../utils/localVideo.js';
	import {
		getPayVideoPlayerCoverUrl,
		getPayVideoPlayerDefaults,
		getPayVideoPlayerVideoUrl,
		normalizePayVideoPlayerData,
	} from '../utils/payVideoPlayer.js';
	import {
		createUploadTasks,
		updateUploadTaskProgress,
		uploadHeaderMediaFiles,
	} from '../utils/uploadHeaderMedia.js';

	export default {
		name: 'GPayVideoPlayerEditModal',
		components: { PayMediaEditForm, UploadProgressPanel },
		props: {
			visible: {
				type: Boolean,
				default: false,
			},
			data: {
				type: Object,
				default: () => getPayVideoPlayerDefaults(),
			},
		},
		emits: ['update:visible', 'confirm'],
		data() {
			return {
				form: getPayVideoPlayerDefaults(),
				isUploading: false,
				uploadTasks: [],
				previewStarted: false,
			};
		},
		computed: {
			coverUrl() {
				return getPayVideoPlayerCoverUrl(this.form);
			},
			previewVideoUrl() {
				return getPayVideoPlayerVideoUrl(this.form);
			},
		},
		watch: {
			visible(value) {
				if (value) {
					this.initForm();
					this.resetPreviewVideo();
				} else {
					this.pausePreviewVideo();
				}
			},
			previewVideoUrl() {
				this.resetPreviewVideo();
			},
		},
		beforeUnmount() {
			this.pausePreviewVideo();
		},
		methods: {
			initForm() {
				this.form = normalizePayVideoPlayerData(this.data);
			},
			resetPreviewVideo() {
				this.pausePreviewVideo();
				this.previewStarted = false;
			},
			pausePreviewVideo() {
				const video = this.$refs.previewVideoEl;
				if (video) {
					video.pause();
				}
			},
			togglePreviewVideo() {
				const video = this.$refs.previewVideoEl;
				if (!video || !this.previewVideoUrl) return;
				this.previewStarted = true;
				this.$nextTick(() => {
					this.$refs.previewVideoEl?.play?.().catch?.(() => {});
				});
			},
			onPreviewVideoEnded() {
				const video = this.$refs.previewVideoEl;
				if (video) {
					video.currentTime = 0;
				}
			},
			triggerCoverSelect() {
				this.$refs.coverInput?.click();
			},
			triggerVideoSelect() {
				this.$refs.videoInput?.click();
			},
			replaceVideo(nextVideo) {
				if (this.form.video?.local) {
					revokeLocalVideoItem(this.form.video);
				}
				this.form.video = normalizeLocalVideoItem(nextVideo);
				if (this.form.cover?.local) {
					revokeLocalImageUrl(this.form.cover);
				}
				this.form.cover = { id: '', filename: '', previewUrl: '', url: '', local: false };
			},
			async onCoverSelect(event) {
				const file = event.target.files?.[0];
				event.target.value = '';
				if (!file || !isLocalImageFile(file)) return;
				if (file.size > MAX_LOCAL_IMAGE_SIZE) return;
				this.isUploading = true;
				this.uploadTasks = createUploadTasks([file]);
				try {
					const [item] = await uploadHeaderMediaFiles(this, {
						type: 'image',
						files: [file],
						onProgress: payload => updateUploadTaskProgress(this.uploadTasks, payload),
					});
					if (this.form.cover?.local) {
						revokeLocalImageUrl(this.form.cover);
					}
					this.form.cover = item;
				} catch (error) {
					this.$message?.error?.('上传失败');
				} finally {
					this.isUploading = false;
					this.uploadTasks = [];
				}
			},
			async onVideoSelect(event) {
				const file = event.target.files?.[0];
				event.target.value = '';
				if (!file) return;
				const [validFile] = filterValidLocalVideoList([file], 1);
				if (!validFile) return;
				this.isUploading = true;
				this.uploadTasks = createUploadTasks([validFile]);
				try {
					const [item] = await uploadHeaderMediaFiles(this, {
						type: 'video',
						files: [validFile],
						onProgress: payload => updateUploadTaskProgress(this.uploadTasks, payload),
					});
					this.replaceVideo(item);
				} catch (error) {
					this.$message?.error?.('上传失败');
				} finally {
					this.isUploading = false;
					this.uploadTasks = [];
				}
			},
			onCancel() {
				this.$emit('update:visible', false);
			},
			onConfirm() {
				this.$emit('confirm', normalizePayVideoPlayerData({ ...this.form }));
				this.$emit('update:visible', false);
			},
		},
	};
</script>

<style scoped lang="less">
	@import '../styles/edit-float-panel.less';

	.edit-float-panel(g-pay-video-player-edit__panel);
	.edit-float-toolbar(g-pay-video-player-edit__toolbar);
	.edit-float-action(g-pay-video-player-edit__action);

	.g-pay-video-player-edit {
		padding: 8px 0 12px;
		border-top: 1px solid #f0f0f0;
		margin-top: 8px;
	}

	.g-pay-video-player-edit__preview {
		margin: 0 auto 16px;
		max-width: 100%;
	}

	.g-pay-video-player-edit__shell {
		position: relative;
		width: 100%;
		aspect-ratio: 16 / 9;
		overflow: hidden;
		background: #111;
	}

	.g-pay-video-player-edit__cover {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}

	.g-pay-video-player-edit__video {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: contain;
		background: #000;
		opacity: 0;
		pointer-events: none;

		&.is-visible {
			opacity: 1;
			pointer-events: auto;
			z-index: 2;
		}
	}

	.g-pay-video-player-edit__overlay {
		position: absolute;
		inset: 0;
		background: linear-gradient(180deg, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0.55) 100%);
		pointer-events: none;
	}

	.g-pay-video-player-edit__content {
		position: relative;
		z-index: 1;
		height: 100%;
	}

	.g-pay-video-player-edit__duration {
		position: absolute;
		right: 16px;
		bottom: 16px;
		padding: 2px 6px;
		border-radius: 4px;
		background: rgba(0, 0, 0, 0.55);
		color: #fff;
		font-size: 11px;
		line-height: 1.4;
	}

	.g-pay-video-player-edit__play {
		position: absolute;
		left: 50%;
		top: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 44px;
		height: 44px;
		padding: 0;
		border: none;
		transform: translate(-50%, -50%);
		border-radius: 50%;
		background: rgba(0, 0, 0, 0.45);
		cursor: pointer;

		&:disabled {
			cursor: not-allowed;
			opacity: 0.45;
		}
	}

	.g-pay-video-player-edit__triangle {
		display: block;
		width: 0;
		height: 0;
		margin-left: 4px;
		border-top: 8px solid transparent;
		border-bottom: 8px solid transparent;
		border-left: 12px solid #fff;
	}

	.g-pay-video-player-edit__file-input {
		display: none;
	}
</style>

<style lang="less">
	@import '../styles/edit-float-panel.less';

	.g-pay-video-player-edit-modal {
		.edit-modal-body();
		.edit-modal-scroll();
	}
</style>
